import React, { useRef, useState } from "react";
import { api, API_BASE } from "../lib/api";
import { Camera, X, Loader2, AlertTriangle, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";

/**
 * Photo gallery for a job, with separate sections for "pre_inspection" and "wrap_progress".
 * - Uses fetch-as-blob to display images (auth required).
 * - Allows uploads from admin or assigned installer.
 *
 * When `itemId` is passed, only shows photos attached to that job_item and
 * scopes new uploads to that item too. When null/empty, shows the whole-job
 * "General" gallery.
 */
export default function JobPhotos({ job, onUpdate, canEdit = true, itemId = "" }) {
  const allPhotos = job.photos || [];
  const scoped = itemId
    ? allPhotos.filter((p) => (p.item_id || "") === itemId)
    : allPhotos.filter((p) => !p.item_id);
  const wrap = scoped.filter((p) => p.category === "wrap_progress");
  const inspect = scoped.filter((p) => p.category === "pre_inspection");

  return (
    <div className="space-y-5">
      <PhotoSection
        title="Pre-inspection Damage"
        helper="Document any existing damage before work begins."
        category="pre_inspection"
        photos={inspect}
        job={job}
        canEdit={canEdit}
        onUpdate={onUpdate}
        accent="amber"
        itemId={itemId}
      />
      <PhotoSection
        title="Wrap Progress"
        helper="Before / during / after wrap photos."
        category="wrap_progress"
        photos={wrap}
        job={job}
        canEdit={canEdit}
        onUpdate={onUpdate}
        accent="blue"
        itemId={itemId}
      />
    </div>
  );
}

function PhotoSection({ title, helper, category, photos, job, onUpdate, canEdit, accent = "blue", itemId = "" }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const upload = async (files) => {
    if (!files?.length) return;
    setUploading(true);
    try {
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        fd.append("category", category);
        if (itemId) fd.append("item_id", itemId);
        await api.post(`/jobs/${job.id}/photos`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      }
      toast.success("Photo(s) uploaded");
      onUpdate?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const remove = async (photoId) => {
    if (!window.confirm("Delete this photo?")) return;
    try {
      await api.delete(`/jobs/${job.id}/photos/${photoId}`);
      toast.success("Removed");
      onUpdate?.();
    } catch (e) {
      toast.error("Failed to delete");
    }
  };

  const accentRing = accent === "amber" ? "border-amber-400/30 bg-amber-500/5" : "border-blue-400/30 bg-blue-500/5";
  const accentIcon = accent === "amber" ? "text-amber-300" : "text-blue-300";

  return (
    <div className={`rounded-2xl border ${accentRing} p-4`}>
      <div className="flex items-start justify-between mb-3">
        <div>
          <div className="flex items-center gap-2">
            {accent === "amber" ? <AlertTriangle className={`w-4 h-4 ${accentIcon}`} /> : <ImageIcon className={`w-4 h-4 ${accentIcon}`} />}
            <h4 className="font-heading text-base text-white">{title}</h4>
          </div>
          <p className="text-xs text-slate-400 mt-0.5">{helper}</p>
        </div>
        {canEdit && (
          <button
            data-testid={`upload-${category}-button`}
            onClick={() => inputRef.current?.click()}
            disabled={uploading}
            className="flex items-center gap-1.5 text-xs bg-white text-black px-3 py-1.5 rounded-full font-semibold hover:bg-slate-100 disabled:opacity-50"
          >
            {uploading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Camera className="w-3 h-3" />}
            Add photo
          </button>
        )}
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          multiple
          className="hidden"
          data-testid={`upload-${category}-input`}
          onChange={(e) => upload(Array.from(e.target.files || []))}
        />
      </div>

      {photos.length === 0 ? (
        <div className="text-xs text-slate-500 italic py-4 text-center border border-dashed border-white/10 rounded-xl">
          No photos yet.
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
          {photos.map((p) => (
            <PhotoThumb key={p.id} photo={p} canDelete={canEdit} onDelete={() => remove(p.id)} />
          ))}
        </div>
      )}
    </div>
  );
}

function PhotoThumb({ photo, canDelete, onDelete }) {
  const [blobUrl, setBlobUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const ai = photo.ai_analysis;

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get(`/files/${photo.path}`, { responseType: "blob" });
        if (cancelled) return;
        const url = URL.createObjectURL(res.data);
        setBlobUrl(url);
      } catch {/* ignore */}
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => {
      cancelled = true;
      if (blobUrl) URL.revokeObjectURL(blobUrl);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [photo.path]);

  return (
    <>
      <div className="space-y-1">
        <div className="relative group aspect-square rounded-xl overflow-hidden border border-white/10 bg-white/[0.03] min-h-[100px]">
          {loading ? (
            <div className="absolute inset-0 flex items-center justify-center text-slate-600">
              <Loader2 className="w-4 h-4 animate-spin" />
            </div>
          ) : blobUrl ? (
            <img
              src={blobUrl}
              alt={photo.filename}
              onClick={() => setOpen(true)}
              className="w-full h-full object-cover cursor-zoom-in"
            />
          ) : (
            <div className="absolute inset-0 flex items-center justify-center text-slate-600 text-xs">
              no preview
            </div>
          )}
          {ai && (
            <div
              className={`absolute top-1 left-1 px-1.5 py-0.5 rounded-full text-[9px] font-semibold uppercase tracking-wider ${
                ai.damage_found ? "bg-amber-500/90 text-black" : "bg-emerald-500/90 text-black"
              }`}
              title={ai.summary}
            >
              AI · {ai.damage_found ? "damage" : "clean"}
            </div>
          )}
          {canDelete && (
            <button
              onClick={onDelete}
              className="absolute top-1 right-1 p-1 rounded-full bg-black/70 text-white opacity-0 group-hover:opacity-100 transition"
            >
              <X className="w-3 h-3" />
            </button>
          )}
        </div>
        {ai?.summary && (
          <div className={`text-[10px] leading-snug px-1 ${ai.damage_found ? "text-amber-300" : "text-emerald-300"}`}>
            {ai.summary}
          </div>
        )}
      </div>
      {open && blobUrl && (
        <div onClick={() => setOpen(false)} className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4">
          <img src={blobUrl} alt={photo.filename} className="max-h-full max-w-full rounded-xl" />
        </div>
      )}
    </>
  );
}
